/**
 * ⭐ Meal Prep and groceries (Nutrition phase 4, owner 2026-10-09; approved wireframe
 * `prototype/meal-prep-wireframe.html`). Pure: recipes, batch scaling, what the batches
 * need, and how the buying list follows them. The store reads and writes.
 *
 * THE RULES
 *  · Preparing records a batch — it never logs eating. Buying never ticks a habit.
 *  · Amounts scale by servings ÷ what the recipe makes.
 *  · The same ingredient (name + state) with COMPATIBLE units adds up: g↔kg, ml↔l↔tsp↔tbsp↔cup,
 *    pcs with pcs. Grams and pieces never add — they stay separate items.
 *  · An item you haven't touched follows its batches. One you ticked or edited is never
 *    overwritten: the change waits for review (Update / Keep). Manual items are never touched.
 */

export type Unit = 'g' | 'kg' | 'ml' | 'l' | 'tsp' | 'tbsp' | 'cup' | 'pcs';
export const UNITS: readonly Unit[] = ['g', 'kg', 'ml', 'l', 'tsp', 'tbsp', 'cup', 'pcs'];
type Family = 'mass' | 'volume' | 'count';
const UNIT_INFO: Record<Unit, { family: Family; base: number }> = {
  g: { family: 'mass', base: 1 }, kg: { family: 'mass', base: 1000 },
  ml: { family: 'volume', base: 1 }, l: { family: 'volume', base: 1000 },
  tsp: { family: 'volume', base: 5 }, tbsp: { family: 'volume', base: 15 }, cup: { family: 'volume', base: 240 },
  pcs: { family: 'count', base: 1 },
};
export const familyOf = (u: Unit | null): Family | null => (u ? UNIT_INFO[u].family : null);
export const toBase = (amount: number, u: Unit) => amount * UNIT_INFO[u].base;

export interface Ingredient {
  id: string;
  /** Null = "some" (amount not known). */
  amount: number | null;
  unit: Unit | null;
  name: string;
  state?: string;
}
export interface Recipe {
  /** How many servings the recipe makes. */
  makes: number;
  prepMinutes?: number | null;
  ingredients: Ingredient[];
  steps: string[];
}

export const RECIPE_MAX_MAKES = 200;
export const round2 = (n: number) => Math.round(n * 100) / 100;

/** Read a stored recipe defensively; anything malformed is dropped, never guessed. */
export function readRecipe(raw: unknown): Recipe | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const makes = Number(o.makes);
  if (!Number.isFinite(makes) || makes <= 0) return null;
  const ingredients = (Array.isArray(o.ingredients) ? o.ingredients : []).flatMap((x): Ingredient[] => {
    if (!x || typeof x !== 'object') return [];
    const i = x as Record<string, unknown>;
    const name = typeof i.name === 'string' ? i.name.trim().slice(0, 120) : '';
    if (!name || typeof i.id !== 'string') return [];
    const amount = i.amount === null || i.amount === undefined || i.amount === '' ? null : Number(i.amount);
    const unit = typeof i.unit === 'string' && (UNITS as readonly string[]).includes(i.unit) ? (i.unit as Unit) : null;
    const state = typeof i.state === 'string' && i.state.trim() ? i.state.trim().slice(0, 60) : undefined;
    return [{ id: i.id, name, amount: amount !== null && Number.isFinite(amount) && amount > 0 ? amount : null, unit, ...(state ? { state } : {}) }];
  });
  const steps = (Array.isArray(o.steps) ? o.steps : []).filter((s): s is string => typeof s === 'string' && !!s.trim()).map(s => s.trim().slice(0, 1000));
  const prep = o.prepMinutes === null || o.prepMinutes === undefined ? null : Number(o.prepMinutes);
  return { makes: Math.min(makes, RECIPE_MAX_MAKES), prepMinutes: prep !== null && Number.isFinite(prep) && prep > 0 ? Math.round(prep) : null, ingredients, steps };
}

/** A recipe with at least one ingredient — what Meal Prep can build a list from. */
export const hasIngredients = (r: Recipe | null): r is Recipe => !!r && r.ingredients.length > 0;

export interface Batch {
  id: string;
  mealId: string;
  servings: number;
  prepDate: string;
  status: 'planned' | 'prepared';
  preparedAt: string | null;
  mealRevision: number;
  /** The recipe the batch was made from (null = the meal had none). */
  recipe: Recipe | null;
  /** Ingredient ids ticked "I have it". */
  have: string[];
}

/** servings ÷ what the recipe makes. */
export const batchFactor = (servings: number, recipe: Recipe | null) => (recipe ? servings / recipe.makes : 1);

/** An ingredient's amount for a batch (null stays null). */
export const scaledAmount = (i: Ingredient, factor: number) => (i.amount === null ? null : round2(i.amount * factor));

const norm = (s: string | undefined) => (s ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
/** The merge key: same ingredient + state + unit family (no unit = its own family). */
export const ingredientKey = (name: string, state: string | undefined, unit: Unit | null) =>
  `${norm(name)}|${norm(state)}|${familyOf(unit) ?? 'none'}`;

export interface NeedSource { batchId: string; ingredientId: string; amount: number | null; unit: Unit | null }
export interface Need {
  key: string;
  name: string;
  state?: string;
  /** Sum of the known amounts, in the family's base unit (g · ml · pcs); null when none are known. */
  base: number | null;
  /** At least one source didn't say how much. */
  someUnknown: boolean;
  sources: NeedSource[];
}

/** What the TO-PREPARE batches need, merged by key (prepared batches are done; "I have it" stays off). */
export function batchNeeds(batches: readonly Batch[]): Map<string, Need> {
  const out = new Map<string, Need>();
  for (const b of batches) {
    if (b.status !== 'planned' || !b.recipe) continue;
    const factor = batchFactor(b.servings, b.recipe);
    for (const ing of b.recipe.ingredients) {
      if (b.have.includes(ing.id)) continue;
      const key = ingredientKey(ing.name, ing.state, ing.unit);
      const amount = scaledAmount(ing, factor);
      const n = out.get(key) ?? { key, name: ing.name.trim(), ...(ing.state ? { state: ing.state } : {}), base: null, someUnknown: false, sources: [] };
      if (amount !== null && ing.unit) n.base = round2((n.base ?? 0) + toBase(amount, ing.unit));
      else n.someUnknown = true;
      n.sources.push({ batchId: b.id, ingredientId: ing.id, amount, unit: ing.unit });
      out.set(key, n);
    }
  }
  return out;
}

/**
 * How to show an amount: in the sources' own unit when they all share one ("1.5 tsp"),
 * else in the family's base, stepped up when large (1,250 g → 1.25 kg; 1,500 ml → 1.5 l).
 */
export function displayAmount(base: number | null, sources: readonly NeedSource[]): { amount: number | null; unit: Unit | null } {
  if (base === null) return { amount: null, unit: sources.find(s => s.unit)?.unit ?? null };
  const units = [...new Set(sources.filter(s => s.amount !== null && s.unit).map(s => s.unit as Unit))];
  // One shared unit: keep it ("1.5 tsp") — except grams / millilitres step up at 1,000.
  if (units.length === 1 && units[0] !== 'g' && units[0] !== 'ml') return { amount: round2(base / UNIT_INFO[units[0]].base), unit: units[0] };
  const fam = familyOf(units[0] ?? null);
  if (fam === 'mass') return base >= 1000 ? { amount: round2(base / 1000), unit: 'kg' } : { amount: round2(base), unit: 'g' };
  if (fam === 'volume') return base >= 1000 ? { amount: round2(base / 1000), unit: 'l' } : { amount: round2(base), unit: 'ml' };
  return { amount: round2(base), unit: 'pcs' };
}

/** The base amount of what's shown (for comparing an edited item with a review). */
export const displayToBase = (amount: number | null, unit: Unit | null) => (amount === null || !unit ? null : round2(toBase(amount, unit)));

export interface GroceryItem {
  id: string;
  name: string;
  amount: number | null;
  unit: Unit | null;
  state: string | null;
  /** Linked items only; null = added by hand (never touched automatically). */
  key: string | null;
  sources: NeedSource[];
  /** The need (base units) this item was last agreed at. */
  basis: number | null;
  checked: boolean;
  edited: boolean;
  reviewPending: boolean;
  /** The new need (base units) awaiting review; null with `reviewPending` = no longer needed. */
  reviewAmount: number | null;
}

export type GroceryInsert = Omit<GroceryItem, 'id'>;
export interface GroceryPlan {
  insert: GroceryInsert[];
  update: { id: string; patch: Partial<GroceryInsert> }[];
  remove: string[];
}

const same = (a: number | null, b: number | null) => (a === null || b === null ? a === b : Math.abs(a - b) < 0.005);
const sameSources = (a: readonly NeedSource[], b: readonly NeedSource[]) =>
  a.length === b.length && a.every((s, i) => s.batchId === b[i].batchId && s.ingredientId === b[i].ingredientId && same(s.amount, b[i].amount) && s.unit === b[i].unit);

/**
 * Bring the list in line with the batches' needs — touching only what it may:
 *  · untouched linked items follow (new amount, sources) or go when no longer needed;
 *  · ticked / edited linked items keep their amount — a change becomes a REVIEW;
 *  · manual items are never in the plan;
 *  · a need with no item gets one.
 */
export function reconcileGroceries(items: readonly GroceryItem[], needs: ReadonlyMap<string, Need>): GroceryPlan {
  const plan: GroceryPlan = { insert: [], update: [], remove: [] };
  const seen = new Set<string>();
  for (const it of items) {
    if (!it.key) continue;
    seen.add(it.key);
    const need = needs.get(it.key);
    const touched = it.checked || it.edited;
    if (!touched) {
      if (!need) { plan.remove.push(it.id); continue; }
      const shown = displayAmount(need.base, need.sources);
      if (!same(it.basis, need.base) || !sameSources(it.sources, need.sources) || it.reviewPending) {
        plan.update.push({ id: it.id, patch: { amount: shown.amount, unit: shown.unit, sources: need.sources, basis: need.base, reviewPending: false, reviewAmount: null } });
      }
      continue;
    }
    // Ticked or edited: never overwrite — offer the change.
    if (!need) {
      // Already bought and no longer needed (e.g. the batch was prepared): nothing to decide.
      if (it.checked) { if (it.reviewPending) plan.update.push({ id: it.id, patch: { reviewPending: false, reviewAmount: null } }); continue; }
      // Already "kept" (no basis, no sources) or already asked: leave it.
      if (it.basis === null && !it.sources.length) continue;
      if (it.reviewPending && it.reviewAmount === null && !it.sources.length) continue;
      plan.update.push({ id: it.id, patch: { sources: [], reviewPending: true, reviewAmount: null } });
      continue;
    }
    const changed = !same(it.basis, need.base);
    const patch: Partial<GroceryInsert> = {};
    if (!sameSources(it.sources, need.sources)) patch.sources = need.sources;
    if (changed && (!it.reviewPending || !same(it.reviewAmount, need.base))) { patch.reviewPending = true; patch.reviewAmount = need.base; }
    if (!changed && it.reviewPending) { patch.reviewPending = false; patch.reviewAmount = null; }
    if (Object.keys(patch).length) plan.update.push({ id: it.id, patch });
  }
  for (const [key, need] of needs) {
    if (seen.has(key)) continue;
    const shown = displayAmount(need.base, need.sources);
    plan.insert.push({
      name: need.name, amount: shown.amount, unit: shown.unit, state: need.state ?? null, key,
      sources: need.sources, basis: need.base, checked: false, edited: false, reviewPending: false, reviewAmount: null,
    });
  }
  return plan;
}

/** "1.25 kg", "3 pcs", "some" — for an item or a need. */
export function amountText(amount: number | null, unit: Unit | null): string {
  if (amount === null) return 'some';
  const n = Number.isInteger(amount) ? String(amount) : String(round2(amount));
  return unit ? `${n} ${unit}` : n;
}

// ── the editor's form ────────────────────────────────────────────────────────

/** The editable form of a recipe (strings while typing). */
export interface RecipeDraft {
  makes: string;
  prep: string;
  rows: { id: string; amount: string; unit: Unit | ''; name: string; state: string }[];
  steps: string[];
}

export const newIngredientId = () => `ing-${Math.random().toString(36).slice(2, 10)}`;

export function toRecipeDraft(r: Recipe | null | undefined): RecipeDraft {
  if (!r) return { makes: '1', prep: '', rows: [], steps: [] };
  return {
    makes: String(r.makes),
    prep: r.prepMinutes ? String(r.prepMinutes) : '',
    rows: r.ingredients.map(i => ({ id: i.id, amount: i.amount === null ? '' : String(i.amount), unit: i.unit ?? '', name: i.name, state: i.state ?? '' })),
    steps: [...r.steps],
  };
}

/**
 * The recipe a form describes — null when it holds no ingredient and no step (an empty,
 * opened section is "no recipe"). A recipe needs to say how many servings it makes.
 */
export function fromRecipeDraft(d: RecipeDraft): { ok: true; recipe: Recipe | null } | { ok: false; error: string } {
  const rows = d.rows.filter(r => r.name.trim());
  const steps = d.steps.map(s => s.trim()).filter(Boolean);
  if (!rows.length && !steps.length) return { ok: true, recipe: null };
  const makes = Number(d.makes);
  if (!Number.isFinite(makes) || makes <= 0 || makes > RECIPE_MAX_MAKES) return { ok: false, error: 'Say how many servings the recipe makes.' };
  const recipe = readRecipe({
    makes, prepMinutes: d.prep.trim() ? Number(d.prep) : null, steps,
    ingredients: rows.map(r => ({ id: r.id, name: r.name, amount: r.amount.trim() ? Number(r.amount) : null, unit: r.unit || null, state: r.state })),
  });
  return recipe ? { ok: true, recipe } : { ok: false, error: 'Say how many servings the recipe makes.' };
}
